// @flow

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link } from 'gatsby'
import * as React from 'react'

import { colors, media, variables } from '@toggl/style'

import * as Button from '../button'
import { IconCheck } from '../icons/IconCheck'
import { IconInfo } from '../icons/IconInfo'
import * as text from '../text'

type FeatureType = {
  +id: string,
  +title: string,
  +description: {
    +link?: string,
    +content: string,
  },
  +free: boolean,
  +starter: boolean,
  +premium: boolean,
  +enterprise: boolean,
}

type Props = {
  +className?: string,
  +features: $ReadOnlyArray<{
    +header: string,
    +entries: $ReadOnlyArray<FeatureType>,
  }>,
  +initialVisibleGroups?: number,
  +showMoreText?: string,
  +title: string,
}

export default function PlanComparisonTable({
  className,
  features,
  initialVisibleGroups,
  showMoreText,
  title,
}: Props) {
  const [numberOfVisibleGroups, setNumberOfVisibleGroups] = React.useState(
    initialVisibleGroups || features.length
  )

  const showAllFeatures = () => {
    setNumberOfVisibleGroups(features.length)
  }

  return (
    <Wrapper className={className}>
      <Title color={colors.almostWhite}>{title}</Title>
      <Table>
        <PlanGroup>
          <div />
          <PlanCTA>
            <PlanName>Free</PlanName>
            <Link to="/track/signup/">
              <Button.Alternate>Get started</Button.Alternate>
            </Link>
          </PlanCTA>
          <PlanCTA>
            <PlanName>Starter</PlanName>
            <Link to="/track/signup/">
              <Button.Alternate>Get started</Button.Alternate>
            </Link>
          </PlanCTA>
          <PlanCTAPremium>
            <PlanName>Premium</PlanName>
            <Link to="/track/signup/">
              <Button.Primary background={colors.pink}>
                Get started
              </Button.Primary>
            </Link>
          </PlanCTAPremium>
          <PlanCTA>
            <PlanName>Enterprise</PlanName>
            <Link to="/track/demo-request/?source=pricing-table-full">
              <Button.Alternate>Talk to Sales</Button.Alternate>
            </Link>
          </PlanCTA>
        </PlanGroup>

        {features.map(
          ({ header, entries }, indx) =>
            indx < numberOfVisibleGroups && (
              <FeatureGroup
                key={`feature_group_${indx}`}
                name={header}
                features={entries}
                isFourth={indx === 3}
              />
            )
        )}
      </Table>
      {numberOfVisibleGroups !== features.length && showMoreText && (
        <ButtonWrapper>
          <Button.Secondary caretDirection="down" onClick={showAllFeatures}>
            {showMoreText}
          </Button.Secondary>
        </ButtonWrapper>
      )}
    </Wrapper>
  )
}

const FeatureGroup = ({ name, features, isFourth }) => (
  <React.Fragment>
    {isFourth && (
      <CTAItem>
        <text.H3 color={colors.beige}>
          Toggl Track is all about saving your time and money
        </text.H3>
        <div>
          <Link to="/track/signup/">
            <Button.Primary>Try Track for free</Button.Primary>
          </Link>
          <Link to="/track/demo-request/?source=pricing-table-full">
            <Button.Secondary caretDirection="right">
              Talk to Sales
            </Button.Secondary>
          </Link>
        </div>
      </CTAItem>
    )}
    <GroupName>{name}</GroupName>
    {features.map((feature) => (
      <Feature key={feature.id} {...feature} />
    ))}
  </React.Fragment>
)

const ButtonWrapper = styled.div`
  margin-top: 20px;
  ${media.mq[1]} {
    margin-top: 60px;
  }
`

const gridStyles = css`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr repeat(4, 30px);
  grid-gap: 1px;
  background: ${colors.lightGray};
  ${media.mq[1]} {
    grid-template-columns: 1fr repeat(4, 100px);
  }
  ${media.mq[2]} {
    grid-template-columns: 1fr repeat(4, 165px);
  }
`

const PlanCTA = styled.div`
  padding: 6px;
  ${media.mq[1]} {
    padding: 12px;
  }
`

const PlanCTAPremium = styled(PlanCTA)`
  background: ${colors.beige};
  position: relative;
  ${media.mq[1]} {
    &:before {
      content: 'Best value';
      padding: 8px;
      background: ${colors.pink};
      width: 100%;
      position: absolute;
      color: ${colors.beige};
      bottom: 100%;
      left: 0;
      text-align: center;
    }
  }
`

const Table: React.ComponentType<any> = styled.div`
  ${gridStyles};
  width: calc(100% + 60px);
  ${media.mq[1]} {
    width: 100%;
  }
`

const PlanGroup = styled.div`
  ${gridStyles};
  grid-column: 1 / -1;
  box-shadow: 0 0 2px ${colors.fadedPurpleAlt};
  > * {
    background: ${({ isPremium }) =>
      isPremium ? colors.beige : colors.almostWhite};
  }
  a {
    display: none;
  }
  ${media.mq[1]} {
    position: sticky;
    top: ${variables.headerHeightTablet}px;
    z-index: 20;
  }
  ${media.mq[2]} {
    top: ${variables.headerHeightFull}px;
    a {
      display: block;
      button {
        height: 45px;
        padding: 12px 25px;
        font-size: 1rem;
        width: 100%;
        justify-content: center;
      }
    }
  }
`

const PlanName = styled.div`
  ${text.heading4};
  font-size: 0.875rem;
  color: ${colors.darkPurple};
  transform: rotate(180deg);
  writing-mode: vertical-lr;
  min-height: 65px;
  ${media.mq[1]} {
    transform: none;
    writing-mode: unset;
    text-align: center;
    min-height: auto;
  }
  ${media.mq[2]} {
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
  }
`

const GroupName = styled(text.H5)`
  padding: 15px;
  grid-column: span 5;
  background: ${colors.fadedPurpleAlt};
  color: ${colors.almostWhite};
`

const Wrapper: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Title: React.ComponentType<any> = styled(text.H2)`
  margin: 20px 0 35px;
  ${media.mq[1]} {
    margin: 30px 0 50px;
  }
  ${media.mq[2]} {
    margin: 50px 0 100px;
  }
`

const Feature = ({
  id,
  title,
  description,
  free,
  starter,
  premium,
  enterprise,
}) => {
  return (
    <React.Fragment>
      <FeatureTitle>
        {description.link ? (
          <Link to={description.link}>
            <span>{title}</span>
          </Link>
        ) : (
          <span>{title}</span>
        )}

        <span data-title={description.content}>
          <IconInfo color={colors.purple60} />
        </span>
      </FeatureTitle>
      {[free, starter, premium, enterprise].map((plan, i) => (
        <Check
          key={`${id}_plan_${i}`}
          desktopLeftPadding={(i + 1) * 5}
          isPremium={i === 2}
        >
          {plan && <IconCheck color={colors.fadedPurple} />}
        </Check>
      ))}
    </React.Fragment>
  )
}

const Check: React.ComponentType<any> = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isPremium }) =>
    isPremium ? colors.beige : colors.almostWhite};
  svg path {
    ${({ isPremium }) =>
      isPremium ? `fill: ${colors.pink}; stroke: ${colors.pink}` : ''};
  }
`

const FeatureTitle = styled.div`
  display: flex;
  column-gap: 10px;
  background: ${colors.almostWhite};
  ${text.paragraph3};
  font-size: 0.9rem;
  padding: 15px;
  color: ${colors.darkPurple};
  a {
    > span {
      color: ${colors.fadedPurpleAlt};
      border-bottom: 1px dashed;
    }
    &:hover > span {
      color: ${colors.pink};
    }
  }
  span[data-title] {
    position: relative;
    cursor: pointer;
    margin-left: auto;
    &:before,
    &:after {
      opacity: 0;
      visibility: hidden;
      transition: 0.15s ease;
      left: 50%;
      transform: translateX(-50%);
      position: absolute;
    }
    &:before {
      content: '';
      height: 0;
      width: 0;
      border-style: solid;
      border-color: ${colors.pink70} transparent transparent transparent;
      border-width: 8px 8px 0 8px;
      bottom: calc(100% + 2px);
    }
    &:after {
      content: attr(data-title);
      background: ${colors.pink70};
      ${text.paragraph4};
      color: ${colors.darkPurple};
      padding: 15px 20px;
      bottom: calc(100% + 8px);
      width: 250px;
      z-index: 21;
    }
    &:hover {
      svg {
        fill: ${colors.darkPurple};
      }
      &:before,
      &:after {
        visibility: visible;
        opacity: 1;
      }
    }
    ${media.mq[1]} {
      font-size: 1rem;
      &:after {
        width: 350px;
      }
    }
  }
`

const CTAItem = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  background: ${colors.fadedPurple};
  padding: 40px 20px 20px;
  margin-bottom: -1px;
  h3 {
    line-height: 1.1;
  }
  > div {
    margin: 30px auto;
    display: flex;
    flex-wrap: wrap;
    column-gap: 30px;
    row-gap: 15px;
    align-items: center;
    justify-content: center;
    button {
      color: ${colors.almostWhite};
    }
  }
  ${media.mq[2]} {
    display: none;
  }
`
